import { Location } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { SubgroupModel } from 'src/app/models/archive/subgroup.model';
import { GroupService } from 'src/app/services/archive/group.service';
import { SubgroupService } from 'src/app/services/archive/subgroup.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-subgroup',
  templateUrl: './register-subgroup.component.html',
  styleUrls: ['./register-subgroup.component.scss'],
  standalone: false
})
export class RegisterSubgroupComponent implements OnInit {

  disabledForm: boolean = false;
  subgroupId: number = 0;
  formSubgroup: FormGroup = new FormGroup({});
  groupId: number = 0;
  permissionUpdatable: boolean = false;
  isOpened: string | undefined = undefined;
  nameWindow: string = 'Captura Subgrupos';
  projectName = ProjectName.projectName;
  url: string = 'subgroup';
  subgroupContract: SubgroupModel = new SubgroupModel();
  listGroups: any[] = [];

  constructor(private activatedRoute: ActivatedRoute,
    private confirmDialogService: ConfirmDialogService,
    private formBuilder: FormBuilder,
    private groupService: GroupService,
    private headerService: HeaderService,
    private location: Location,
    private subgroupService: SubgroupService,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private windowService: WindowService) {
    this.validateForm(this.subgroupContract);
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.activatedRoute.params.subscribe(params => {
      this.subgroupId = Object.keys(params).length > 0 ? params?.id : null;
    });
    await this.getPermission();
    this.handleInitialState();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  changeGroup(groupId: number) {
    if (!groupId) { return; }
    this.subgroupContract = new SubgroupModel();
    this.subgroupContract.groupId = groupId;
    this.validateForm(this.subgroupContract);
    this.updateFormPermision();
  }

  changeSubgroup(code: string) {
    const groupId = this.formSubgroup.get("groupId")?.value;
    if (groupId == 0 || groupId == null) {
      this.toastHandlerService.showToast(
        'warning',
        `Debe seleccionar el grupo primero.`
      );
      return;
    }
    if (code?.trim() != '') {
      this.getSubgroupByCode(groupId, code);
    } else {
      this.createNewSubgroup();
    }
  }

  createNewSubgroup() {
    if (this.permissionUpdatable) {
      this.subgroupId = 0;
      this.subgroupContract = new SubgroupModel();
      this.validateForm(this.subgroupContract);
      this.updateFormPermision();
      this.location.replaceState(`/${this.url}/register`);
    } else {
      this.updateFormPermision();
      this.toastHandlerService.showToast(
        'error',
        `El usuario no tiene permisos para crear subgrupos. Comuníquese con el administrador del sistema.`
      );
    }
  }

  async delete() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        `No tiene permisos para eliminar un subgrupo. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar el subgrupo?')) {
      try {
        await firstValueFrom(this.subgroupService.deleteSubgroup(this.subgroupContract.subgroupId));
        this.toastHandlerService.showToast(
          'success',
          'El subgrupo fue eliminado exitosamente.'
        );
        this.createNewSubgroup();
      } catch (error) {
        console.error('Error al eliminar el subgrupo.', error);
      }
    }
  }

  async getListGroups() {
    try {
      this.listGroups = await firstValueFrom(this.groupService.getAllGroupsForSelectAsync());
    } catch (error) {
      console.error('Error al obtner la lista de grupos.', error);
    }
  }

  async getSubgroupByCode(groupId: number, code: string) {
    try {
      const response = await firstValueFrom(this.subgroupService.getGroupByCode(groupId, code));
      this.subgroupId = response.subgroupId;
      this.subgroupContract = response;
      this.validateForm(this.subgroupContract);
      this.location.replaceState(`/${this.url}/register/${this.subgroupId}`);
    } catch (error) {
      console.error('Error al obtener el subgrupo por codigo.', error);
    }
  }

  async getSubgroupById() {
    try {
      this.subgroupContract = await firstValueFrom(this.subgroupService.getSubgroupById(this.subgroupId));
      this.validateForm(this.subgroupContract)
    } catch (error) {
      console.error('Error al obtener la subgrupo por id.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await Promise.all([
      this.getListGroups(),
      this.getPermissionProfileByUrl()
    ]);
  }

  async getPermissionProfileByUrl() {
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      this.permissionUpdatable = false;
      console.error('Error al cargar permisos.', error);
    }
  }

  async handleInitialState() {
    if (this.subgroupId !== null && this.subgroupId !== 0) {
      this.getSubgroupById();
    } else {
      this.createNewSubgroup();
    }
  }

  async send() {
    this.validateFormControls();
    if (this.formSubgroup.valid) {
      const modelSubgroup = this.formSubgroup.value as SubgroupModel;
      if (modelSubgroup.groupId == 0 || modelSubgroup.groupId == null) {
        this.toastHandlerService.showToast(
          'warning',
          `Debe seleccionar primero el grupo.`
        );
        return;
      }
      if (this.subgroupId == 0) {
        try {
          const response = await firstValueFrom(this.subgroupService.createSubgroup(modelSubgroup));
          this.subgroupId = response.subgroupId;
          this.subgroupContract = response;
          this.validateForm(this.subgroupContract);
          this.location.replaceState(`/${this.url}/register/${this.subgroupId}`);
          this.toastHandlerService.showToast(
            'success',
            `El subgrupo fue creado exitosamente.`
          );
        } catch (error) {
          console.error('Error al crear el subgrupo.', error);
        }
      } else {
        this.subgroupContract.name = modelSubgroup.name;
        try {
          const response = await firstValueFrom(this.subgroupService.updateSubgroup(this.subgroupContract));
          this.subgroupContract = response;
          this.validateForm(this.subgroupContract);
          this.toastHandlerService.showToast(
            'success',
            `El subgrupo fue modificado exitosamente.`
          );
        } catch (error) {
          console.error('Error al modificar el subgrupo.', error);
        }
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      code: {
        required: 'El campo Código es obligatorio.',
        maxlength: 'El Código no puede superar los 3 caracteres.',
      },
      name: {
        required: 'El campo Nombre es obligatorio.',
        maxlength: 'El Nombre no puede superar los 50 caracteres.',
      },
      groupId: {
        required: 'El campo Grupo es obligatorio.',
        min: 'Debes seleccionar un grupo válido.',
      },
    };

    Object.keys(controlMessages).forEach((controlName) => {
      const formControl = this.formSubgroup.get(controlName);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[controlName];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  updateFormPermision() {
    if (this.permissionUpdatable) {
      this.disabledForm = false;
      setTimeout(() => this.formSubgroup.enable(), 100);
    } else {
      this.disabledForm = true;
      setTimeout(() => this.formSubgroup.disable(), 100);
      setTimeout(() => this.formSubgroup.controls["code"].enable(), 100);
    }
  }

  validateForm(subgroup: SubgroupModel) {
    this.formSubgroup = this.formBuilder.group({
      code: [subgroup.code, [Validators.required, Validators.maxLength(3)]],
      name: [subgroup.name, [Validators.required, Validators.maxLength(50)]],
      groupId: [subgroup.groupId, [Validators.required, Validators.min(1)]]
    });
  }

  validateFormControls() {
    Object.keys(this.formSubgroup.controls).forEach((key) => {
      const control = this.formSubgroup.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
